// src/component/MusicAdminPage.js
import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Stack,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import MusicNoteRoundedIcon from "@mui/icons-material/MusicNoteRounded";

import { db, auth } from "../api/firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { AppThemeContext } from "./AppShell";

const PAYMENT_TERMS = [1, 2, 3, 4];

const SEASON_SLOTS = [
  { value: "winterToSpring", label: "冬至 → 春分" },
  { value: "springToSummer", label: "春分 → 夏至" },
  { value: "summerToAutumn", label: "夏至 → 秋分" },
  { value: "autumnToWinter", label: "秋分 → 冬至" },
];

const normalizeTerms = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((t) => String(t).trim())
    .filter(Boolean);

/**
 * ✅ ここがエラー停止の要（堅牢な日付フォーマット）
 * - Firestore Timestamp / Date / number / string に対応
 * - serverTimestamp() の未解決 FieldValue（ただのオブジェクト）が来ても落ちない
 */
const formatDateSafe = (ts) => {
  if (!ts) return "";

  // Firestore Timestamp
  if (typeof ts?.toDate === "function") {
    const d = ts.toDate();
    return d instanceof Date ? d.toLocaleDateString("ja-JP") : "";
  }

  // JS Date
  if (ts instanceof Date) {
    return ts.toLocaleDateString("ja-JP");
  }

  // number (millis)
  if (typeof ts === "number") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("ja-JP");
  }

  // string (ISO etc.)
  if (typeof ts === "string") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("ja-JP");
  }

  // FieldValue / unknown object
  return "";
};

export default function MusicAdminPage() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};

  const surface = theme.surfaceBg || "#FFFFFF";
  const border = "rgba(0,0,0,0.08)";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";

  const [tracks, setTracks] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // 🔹 TopPage に選択されている trackId
  const [topTrackId, setTopTrackId] = useState(null);

  // 追加ダイアログ
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCreator, setNewCreator] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newSeasonSlot, setNewSeasonSlot] = useState("winterToSpring");
  const [newVisible, setNewVisible] = useState(true);

  // 削除ダイアログ
  const [deleteTarget, setDeleteTarget] = useState(null);

  // --- 管理者チェック ---
  useEffect(() => {
    const checkAdmin = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/");
        return;
      }
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        navigate("/");
        return;
      }
      const data = userDoc.data();
      if (!data.isAdmin) {
        navigate("/");
        return;
      }
      setIsAdmin(true);
    };

    checkAdmin();
  }, [navigate]);

  // --- tracks 購読 ---
  useEffect(() => {
    if (!isAdmin) return;

    const q = query(collection(db, "tracks"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTracks(list);
    });

    return () => unsub();
  }, [isAdmin]);

  // --- TopPage 用の選曲設定を取得 ---
  useEffect(() => {
    if (!isAdmin) return;

    const fetchTopTrack = async () => {
      try {
        const ref = doc(db, "musicSettings", "topPageSeasonalTrack");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          if (data.trackId) setTopTrackId(data.trackId);
        }
      } catch (e) {
        console.error("Failed to load topPageSeasonalTrack:", e);
      }
    };

    fetchTopTrack();
  }, [isAdmin]);

  // --- 表示 ON/OFF ---
  const handleToggleVisible = async (track) => {
    const next = !(track.visible ?? true);
    try {
      await updateDoc(doc(db, "tracks", track.id), { visible: next });
      setTracks((prev) =>
        prev.map((t) => (t.id === track.id ? { ...t, visible: next } : t))
      );
    } catch (e) {
      console.error("Failed to update visible:", e);
    }
  };

  // --- 視聴対象期トグル ---
  const handleToggleTerm = async (track, term) => {
    const termStr = String(term);
    const current = normalizeTerms(track.allowedTerm);
    const has = current.includes(termStr);
    const next = has
      ? current.filter((t) => t !== termStr)
      : [...current, termStr];

    try {
      await updateDoc(doc(db, "tracks", track.id), { allowedTerm: next });
      setTracks((prev) =>
        prev.map((t) => (t.id === track.id ? { ...t, allowedTerm: next } : t))
      );
    } catch (e) {
      console.error("Failed to update allowedTerm:", e);
    }
  };

  // --- TopPage の曲として設定 ---
  const handleSetTopTrack = async (track) => {
    try {
      const ref = doc(db, "musicSettings", "topPageSeasonalTrack");
      await setDoc(ref, {
        trackId: track.id,
        title: track.name,
        src: track.url,
        creator: track.creator,
        seasonSlot: track.seasonSlot || null,
        updatedAt: serverTimestamp(),
      });
      setTopTrackId(track.id);
    } catch (e) {
      console.error("Failed to set topPageSeasonalTrack:", e);
    }
  };

  // --- 追加ダイアログ open/close ---
  const openAddDialog = () => {
    setNewName("");
    setNewCreator("");
    setNewUrl("");
    setNewSeasonSlot("winterToSpring");
    setNewVisible(true);
    setAddOpen(true);
  };

  const closeAddDialog = () => {
    setAddOpen(false);
  };

  /**
   * ✅ ここがエラー停止の要（2点目）
   * addDoc直後に payload を state に混ぜない。
   * payload.createdAt は serverTimestamp() で FieldValue（ただのオブジェクト）なので、
   * それを即レンダリングすると formatDate が踏んで落ちやすい。
   * 反映は onSnapshot に任せる。
   */
  const handleAddTrack = async () => {
    if (!newName.trim() || !newCreator.trim() || !newUrl.trim()) return;

    const payload = {
      name: newName.trim(),
      creator: newCreator.trim(),
      url: newUrl.trim(), // 例: /audio/touji-2025.mp3
      seasonSlot: newSeasonSlot,
      visible: newVisible,
      allowedTerm: [],
      createdAt: serverTimestamp(),
    };

    try {
      await addDoc(collection(db, "tracks"), payload);

      // ❗ここがポイント：手動で setTracks しない（落ちる原因を作らない）
      // setTracks((prev) => [{ id: docRef.id, ...payload }, ...prev]);

      setAddOpen(false);
    } catch (e) {
      console.error("Failed to add track:", e);
    }
  };

  // --- 削除 ---
  const openDeleteDialog = (track) => {
    setDeleteTarget(track);
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
  };

  const handleDeleteTrack = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, "tracks", deleteTarget.id));
      setTracks((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      console.error("Failed to delete track:", e);
    }
  };

  const labelForSeason = (value) =>
    SEASON_SLOTS.find((s) => s.value === value)?.label || "未設定";

  if (!isAdmin) return null;

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, pb: { xs: 3, sm: 4 } }}>
      {/* ヘッダー */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: "0.18em",
              fontSize: 11,
              opacity: 0.8,
              color: textBase,
            }}
          >
            ADMIN / MUSIC
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase }}
          >
            音楽管理
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
            冬至〜春分 / 春分〜夏至 / 夏至〜秋分 / 秋分〜冬至 の4つの期間ごとに、
            トップページで再生する曲を登録します。
            音源はサイトに配置したファイルの URL を指定してください。
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/admin")}
          >
            管理者メニューに戻る
          </Button>
          <Button
            variant="contained"
            startIcon={<MusicNoteRoundedIcon />}
            onClick={openAddDialog}
          >
            音楽を追加
          </Button>
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* 一覧 */}
      <Grid container spacing={2.5}>
        {tracks.map((track) => {
          const allowedSet = new Set(normalizeTerms(track.allowedTerm));
          const createdLabel = track.createdAt
            ? formatDateSafe(track.createdAt)
            : "";

          return (
            <Grid item xs={12} md={6} key={track.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  boxShadow: "0 18px 40px rgba(15,10,40,0.06)",
                  height: "100%",
                }}
              >
                <CardActionArea
                  sx={{
                    p: 2,
                    alignItems: "stretch",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    "&:hover": {
                      boxShadow: "0 22px 50px rgba(15,10,40,0.12)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all .18s ease-out",
                  }}
                >
                  <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                    {/* 上段：タイトル & ボタン */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: textBase,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {track.name || "（曲名未設定）"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}
                        >
                          {track.creator || "（作曲者未設定）"}
                          {createdLabel && ` / 登録日: ${createdLabel}`}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.3,
                            color: "rgba(0,0,0,0.5)",
                            wordBreak: "break-all",
                          }}
                        >
                          URL: {track.url}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.3,
                            color: "rgba(0,0,0,0.65)",
                          }}
                        >
                          期間: {labelForSeason(track.seasonSlot)}
                        </Typography>
                      </Box>

                      {/* 削除ボタン */}
                      <Button
                        variant="text"
                        color="error"
                        startIcon={<DeleteOutlineRoundedIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog(track);
                        }}
                      >
                        削除
                      </Button>
                    </Box>

                    {/* オーディオ */}
                    <Box
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        p: 1,
                        bgcolor: "rgba(255,255,255,0.9)",
                        border: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <audio
                        src={track.url}
                        controls
                        preload="none"
                        style={{ width: "100%" }}
                      />
                    </Box>

                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={track.visible ?? true}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleVisible(track);
                            }}
                          />
                        }
                        label="表示"
                      />
                    </Stack>

                    {/* 視聴対象 */}
                    <Box sx={{ mb: 0.5, fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                      視聴対象
                    </Box>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {PAYMENT_TERMS.map((term) => (
                        <FormControlLabel
                          key={term}
                          control={
                            <Checkbox
                              size="small"
                              checked={allowedSet.has(String(term))}
                              onChange={() => handleToggleTerm(track, term)}
                            />
                          }
                          label={`${term}期`}
                          sx={{
                            m: 0,
                            mr: 1.5,
                            "& .MuiFormControlLabel-label": { fontSize: 12 },
                          }}
                        />
                      ))}
                    </Box>

                    {/* TopPage の曲に設定 */}
                    <Box sx={{ mt: 1.5 }}>
                      <Button
                        variant={track.id === topTrackId ? "contained" : "outlined"}
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetTopTrack(track);
                        }}
                      >
                        {track.id === topTrackId
                          ? "トップページの曲に設定中"
                          : "トップページの曲にする"}
                      </Button>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* 追加ダイアログ */}
      <Dialog open={addOpen} onClose={closeAddDialog} fullWidth maxWidth="sm">
        <DialogTitle>新しい音楽を登録</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: "rgba(0,0,0,0.7)" }}>
            サイトに配置した音源ファイルの URL と、対応する期間を登録します。
          </Typography>
          <TextField
            margin="dense"
            label="曲名"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="作曲者"
            fullWidth
            value={newCreator}
            onChange={(e) => setNewCreator(e.target.value)}
          />
          <TextField
            margin="dense"
            label="音源URL（例: /audio/touji-2025.mp3）"
            fullWidth
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
          />

          <FormControl fullWidth margin="dense">
            <InputLabel id="season-slot-label">適用期間</InputLabel>
            <Select
              labelId="season-slot-label"
              value={newSeasonSlot}
              label="適用期間"
              onChange={(e) => setNewSeasonSlot(e.target.value)}
            >
              {SEASON_SLOTS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={newVisible}
                onChange={(e) => setNewVisible(e.target.checked)}
              />
            }
            label="表示（有効）"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>キャンセル</Button>
          <Button onClick={handleAddTrack} variant="contained">
            登録する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除ダイアログ */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>曲を削除しますか？</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            次の曲を削除します。この操作は取り消せません。
          </Typography>
          {deleteTarget && (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {deleteTarget.name} / {deleteTarget.creator}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>キャンセル</Button>
          <Button onClick={handleDeleteTrack} color="error" variant="contained">
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
